import React, { Ref } from "react"

import classNames from "classnames"

import { exists } from "shared/lib/exists"
import { useEvent } from "shared/lib/useEvent"
import { IconSize } from "shared/types/Icon"
import { SelectOption } from "shared/types/Select"
import { Icon } from "shared/ui/icon/Icon"
import { Tooltip } from "shared/ui/tooltip/Tooltip"

export interface BoxSelectOptionProps<T> {
  type: "radio" | "checkbox"
  name: string
  option: SelectOption<T>
  isSelected: boolean
  isDisabled?: boolean
  onChange: (value: T) => void
  inputRef?: Ref<HTMLButtonElement>
  tabIndex?: number
  onFocus?: () => void
  iconSize?: IconSize
  isIconOnly?: boolean
  useValueAsLabel?: boolean
  optionClassName?: string
  ref?: Ref<HTMLDivElement>
}

function BoxSelectOptionComponent<T>({
  type,
  option,
  isSelected,
  isDisabled,
  onChange,
  inputRef,
  tabIndex,
  onFocus,
  iconSize,
  isIconOnly,
  useValueAsLabel,
  optionClassName,
  ref
}: BoxSelectOptionProps<T>) {
  const handleChange = useEvent(() => {
    if (isDisabled) return
    onChange(option.value)
  })

  const label =
    useValueAsLabel && exists(option.value) ? `${option.value}` : option.label

  const isLabelAndValueEqual = option.label === `${option.value}`

  return (
    <Tooltip
      content={option.label}
      disabled={!useValueAsLabel || isLabelAndValueEqual}
      placement="bottom"
      sideOffset={8}
    >
      <div
        ref={ref}
        className={classNames("box-select-option", optionClassName)}
        data-checked={isSelected ? "" : undefined}
        data-disabled={isDisabled ? "" : undefined}
      >
        <button
          ref={inputRef}
          type="button"
          role={type}
          aria-checked={isSelected}
          aria-label={option.label}
          disabled={isDisabled}
          onClick={handleChange}
          tabIndex={tabIndex}
          onFocus={onFocus}
          className="box-select-option__button"
          id={option.key}
          data-testid={option["data-testid"] || option.key}
        >
          {option.icon ? <Icon name={option.icon} size={iconSize} /> : null}
          {isIconOnly ? null : label || "-"}
        </button>
      </div>
    </Tooltip>
  )
}

export const BoxSelectOption = React.memo(
  BoxSelectOptionComponent
) as typeof BoxSelectOptionComponent
